/**
 * Call Event Component
 *
 * Handles ahoy_call_completed, ahoy_call_missed, and ahoy_voicemail_received events.
 */

import { bold, InlineText, TimelineEvent } from '../components/shared';
import { TIMELINE_DESIGN_TOKENS } from '../design-tokens';
import type { CallPayload } from '../types';
import { EVENT_TYPES } from '../types';
import type { EventComponentProps } from './types';

const CALL_LABELS: Record<string, string> = {
  [EVENT_TYPES.AHOY_CALL_COMPLETED]: 'Call completed',
  [EVENT_TYPES.AHOY_CALL_MISSED]: 'Missed call',
  [EVENT_TYPES.AHOY_VOICEMAIL_RECEIVED]: 'Voicemail received',
};

const META_TEXT_CLASS = `${TIMELINE_DESIGN_TOKENS.typography.subItem} text-secondary`;

function formatCallDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return '';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }
  if (secs > 0 || parts.length === 0) {
    parts.push(`${secs}s`);
  }

  return parts.join(' ');
}

export function CallEvent({ event }: EventComponentProps) {
  const payload = event.payload as CallPayload | undefined;
  const label = CALL_LABELS[event.event_type_name] ?? 'Call';

  const duration =
    event.event_type_name === EVENT_TYPES.AHOY_CALL_COMPLETED &&
    typeof payload?.duration_seconds === 'number'
      ? formatCallDuration(payload.duration_seconds)
      : '';

  return (
    <TimelineEvent ariaLabel={label}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Content
        eventId={event.event_id}
        header={<InlineText>{bold(label)}</InlineText>}
        timestamp={event.occurred_at}
      >
        {payload?.counterparty && (
          <div className={META_TEXT_CLASS}>{payload.counterparty}</div>
        )}
        {payload?.direction && (
          <div className={META_TEXT_CLASS}>{payload.direction}</div>
        )}
        {duration && <div className={META_TEXT_CLASS}>{duration}</div>}
      </TimelineEvent.Content>
    </TimelineEvent>
  );
}
